import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronLeft,
  CircleAlert,
  ExternalLink,
  LayoutDashboard,
  Mail,
  MailOpen,
  MessageSquarePlus,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Search,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  ScrollArea,
  Spinner,
} from "@opencloud/ui";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { api } from "../api.js";
import type { ConversationRecord, ConversationSidebarUpdate } from "@opencloud/agent-protocol";
import { listConversations, updateConversationSidebar } from "../conversations-api.js";
import type { AgentBootstrap } from "../bootstrap.js";
import { agentBrand } from "../brand.js";
import type {
  AgentAppView,
  ConversationSearchResult,
  ConversationThread,
  SessionView,
} from "../types.js";
import type { AgentConversationActivity } from "../use-agent-events.js";
import { AppIcon, ChatIcon } from "./AppIcon.js";

export function Sidebar({
  bootstrap,
  session,
  apps,
  workingAppIds,
  attentionAppIds = new Set(),
  threadsByApp,
  currentApp,
  onHome,
  onNewApp,
  onSelectApp,
  conversations,
  workingConversationIds = new Set(),
  attentionConversationIds = new Set(),
  conversationPreviews = new Map(),
  conversationActivity = new Map(),
  currentConversationId,
  onSelectConversation,
  onConversationChanged,
}: {
  bootstrap: AgentBootstrap;
  session: SessionView;
  apps: readonly AgentAppView[];
  workingAppIds: ReadonlySet<string>;
  attentionAppIds?: ReadonlySet<string>;
  threadsByApp: ReadonlyMap<string, ConversationThread | null>;
  currentApp: AgentAppView | null;
  onHome: () => void;
  onNewApp: () => void;
  conversations?: readonly ConversationRecord[];
  workingConversationIds?: ReadonlySet<string>;
  attentionConversationIds?: ReadonlySet<string>;
  conversationPreviews?: ReadonlyMap<string, string>;
  conversationActivity?: ReadonlyMap<string, AgentConversationActivity>;
  currentConversationId?: string;
  onSelectConversation?: (conversation: ConversationRecord) => void;
  onConversationChanged?: (conversationId: string, conversation: ConversationRecord | null,
    change: ConversationSidebarUpdate) => void;
  onSelectApp: (
    app: AgentAppView,
    location?: { threadId: string; messageId?: string },
  ) => void;
}) {
  const searchId = useId();
  const searchInput = useRef<HTMLInputElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [messageResults, setMessageResults] = useState<
    ConversationSearchResult[]
  >([]);
  const [searchingMessages, setSearchingMessages] = useState(false);
  const [loadedConversations, setLoadedConversations] = useState<ConversationRecord[]>([]);
  const [conversationHistoryLoaded, setConversationHistoryLoaded] = useState(conversations !== undefined);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedConversations, setArchivedConversations] = useState<ConversationRecord[]>([]);
  const [archivedHistoryLoaded, setArchivedHistoryLoaded] = useState(false);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [busyConversationIds, setBusyConversationIds] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState("");
  const activeHistory = conversations ?? loadedConversations;
  const history = showArchived ? archivedConversations : activeHistory;
  const historyLoaded = showArchived ? archivedHistoryLoaded : conversationHistoryLoaded;
  useEffect(() => {
    if (conversations !== undefined) {
      setConversationHistoryLoaded(true);
      return;
    }
    const controller = new AbortController();
    void listConversations(controller.signal).then(records => {
      if (!controller.signal.aborted) {
        setLoadedConversations(records);
        setConversationHistoryLoaded(true);
      }
    }).catch(() => undefined);
    return () => controller.abort();
  }, [conversations, currentConversationId, currentApp?.id]);

  useEffect(() => {
    if (!showArchived || archivedHistoryLoaded) return;
    const controller = new AbortController();
    setLoadingArchived(true);
    setActionError("");
    void listConversations(controller.signal, { archived: true }).then(records => {
      if (!controller.signal.aborted) {
        setArchivedConversations(records);
        setArchivedHistoryLoaded(true);
      }
    }).catch(error => {
      if (!controller.signal.aborted) {
        setActionError(error instanceof Error ? error.message : "Archived chats could not be loaded.");
      }
    }).finally(() => {
      if (!controller.signal.aborted) setLoadingArchived(false);
    });
    return () => controller.abort();
  }, [archivedHistoryLoaded, showArchived]);

  const visibleApps = useMemo(
    () => filterSidebarApps(apps, threadsByApp, query),
    [apps, query, threadsByApp],
  );
  const recentEntries = useMemo(() => {
    const conversationByApp = new Map(history.filter((record) => record.appId)
      .map((record) => [record.appId!, record]));
    return [
      ...visibleApps.filter(app => showArchived
        ? conversationByApp.has(app.id)
        : !historyLoaded || !threadsByApp.get(app.id) || conversationByApp.has(app.id)).map((app) => {
        const thread = threadsByApp.get(app.id) ?? null;
        const record = conversationByApp.get(app.id) ?? null;
        const conversationId = record?.id ?? thread?.id ?? null;
        const activity = conversationId ? conversationActivity.get(conversationId) : undefined;
        const attention = attentionAppIds.has(app.id) || app.state === "failed" ||
          Boolean(conversationId && attentionConversationIds.has(conversationId));
        const working = !attention && (workingAppIds.has(app.id) ||
          Boolean(conversationId && workingConversationIds.has(conversationId)));
        const fallbackAt = record?.updatedAt ?? thread?.updatedAt ?? app.updatedAt ?? "";
        return { app, conversation: record, standalone: false, conversationId, attention, working,
          order: { id: app.id, pinned: record?.pinned ?? false, attention, working, fallbackAt,
            lastUserMessageAt: activity?.lastUserMessageAt ?? null,
            lastAgentMessageAt: activity?.lastAgentMessageAt ?? null } };
      }),
      ...history.filter((conversation) => conversation.kind === "standalone" && conversation.title.toLowerCase().includes(query.trim().toLowerCase()))
        .map((conversation) => {
          const activity = conversationActivity.get(conversation.id);
          const attention = attentionConversationIds.has(conversation.id);
          const working = workingConversationIds.has(conversation.id) && !attention;
          return { app: null, conversation, standalone: true, conversationId: conversation.id, attention, working,
            order: { id: conversation.id, pinned: conversation.pinned, attention, working, fallbackAt: conversation.updatedAt,
              lastUserMessageAt: activity?.lastUserMessageAt ?? null,
              lastAgentMessageAt: activity?.lastAgentMessageAt ?? null } };
        }),
    ].sort((left, right) => compareSidebarConversationOrder(left.order, right.order));
  }, [attentionAppIds, attentionConversationIds, conversationActivity, history, historyLoaded, query, showArchived,
    threadsByApp, visibleApps, workingAppIds, workingConversationIds]);

  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const term = query.trim();
    if (!searchOpen || term.length < 2) {
      setMessageResults([]);
      setSearchingMessages(false);
      return;
    }
    let active = true;
    setSearchingMessages(true);
    const timer = window.setTimeout(() => {
      void api<{ results: ConversationSearchResult[] }>(
        `/v1/search?q=${encodeURIComponent(term)}`,
      )
        .then((response) => {
          if (active) setMessageResults(response.results);
        })
        .catch(() => {
          if (active) setMessageResults([]);
        })
        .finally(() => {
          if (active) setSearchingMessages(false);
        });
    }, 180);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query, searchOpen]);

  async function changeConversation(
    conversationId: string,
    change: Parameters<typeof updateConversationSidebar>[1],
  ) {
    if (busyConversationIds.has(conversationId)) return;
    setBusyConversationIds(current => new Set(current).add(conversationId));
    setActionError("");
    try {
      const result = await updateConversationSidebar(conversationId, change);
      setLoadedConversations(current => result.archived
        ? current.filter(record => record.id !== conversationId)
        : [result.conversation, ...current.filter(record => record.id !== conversationId)]);
      setArchivedConversations(current => result.archived
        ? [result.conversation, ...current.filter(record => record.id !== conversationId)]
        : current.filter(record => record.id !== conversationId));
      onConversationChanged?.(conversationId, result.archived ? null : result.conversation, change);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "The conversation could not be changed.");
    } finally {
      setBusyConversationIds(current => {
        const next = new Set(current);
        next.delete(conversationId);
        return next;
      });
    }
  }

  const brand = agentBrand();
  return (
    <div className="a3-sidebar-inner">
      <div className="a3-sidebar-brand-row">
        <Button
          variant="ghost"
          className="a3-brand a3-brand-button"
          aria-label="Agent home"
          onClick={onHome}
        >
          <img
            src={bootstrap.logoUrl}
            alt=""
            aria-hidden="true"
            width="26"
            height="26"
          />
          <span className="a3-brand-copy">
            <strong>{brand.productName}</strong>
            <small>{brand.agentName}</small>
          </span>
        </Button>
      </div>

      <nav className="a3-conversations" aria-label="Chats and apps">
        <Button className="a3-new-chat" onClick={onNewApp}>
          <MessageSquarePlus aria-hidden="true" />
          New chat
        </Button>
        <div className="a3-sidebar-section-heading">
          <span>{showArchived ? "Archived" : "Chats"}</span>
          {showArchived ? (
            <Button type="button" size="icon" variant="ghost"
              className="a3-icon-button a3-sidebar-search-toggle"
              aria-label="Back to chats" onClick={() => setShowArchived(false)}>
              <ChevronLeft aria-hidden="true" />
            </Button>
          ) : (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="a3-icon-button a3-sidebar-search-toggle"
              aria-label={searchOpen ? "Hide navigation search" : "Search chats and apps"}
              aria-expanded={searchOpen}
              aria-controls={searchId}
              onClick={() => {
                setSearchOpen((open) => {
                  if (open) setQuery("");
                  return !open;
                });
              }}
            >
              <Search aria-hidden="true" />
            </Button>
          )}
        </div>
        {!showArchived && searchOpen ? (
          <div className="a3-conversation-search" id={searchId}>
            <Search aria-hidden="true" />
            <Input
              ref={searchInput}
              type="search"
              value={query}
              placeholder="Search chats and apps"
              aria-label="Search chats and apps"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Escape") return;
                setQuery("");
                setSearchOpen(false);
              }}
            />
          </div>
        ) : null}
        {!showArchived && query.trim().length >= 2 ? (
          <MessageSearchResults
            apps={apps}
            results={messageResults}
            searching={searchingMessages}
            onOpen={(app, result) =>
              onSelectApp(app, {
                threadId: result.threadId,
                messageId: result.messageId,
              })
            }
          />
        ) : null}
        <ScrollArea className="a3-conversation-scroll">
          <ul className="a3-conversation-list">
            {recentEntries.map(({ app, conversation, standalone, conversationId, attention, working, order }) => {
              if (conversation && standalone) {
                return <li className="a3-conversation-item" data-unread={conversation.unread || undefined}
                  key={`conversation:${conversation.id}`}>
                  <Button type="button" variant="ghost" className="a3-conversation" data-kind="chat"
                    aria-current={conversation.id === currentConversationId ? "page" : undefined}
                    title={conversation.title} onClick={() => onSelectConversation ? onSelectConversation(conversation) : window.location.assign(`/?conversation=${encodeURIComponent(conversation.id)}`)}>
                    <span className="a3-sidebar-app-icon"><ChatIcon />
                      {working ? <span className="a3-sidebar-app-progress" title="Working"><Spinner aria-label={`${conversation.title} is working`} /></span>
                        : attention ? <span className="a3-sidebar-app-progress" data-status="attention" role="img" aria-label="Needs attention" title="Needs attention"><CircleAlert aria-hidden="true" /></span>
                        : null}
                    </span>
                    <strong className="a3-sidebar-app-name">{conversation.title}</strong>
                    <span className="a3-sidebar-app-preview">{working ? "Agent is working…" : plainPreview(conversationPreviews.get(conversation.id)) || "Private chat"}</span>
                    <small className="a3-sidebar-app-time">{relativeDate(sidebarOrderTimestamp(order))}</small>
                  </Button>
                  <ConversationActions
                    bootstrap={bootstrap}
                    title={conversation.title}
                    pinned={conversation.pinned}
                    unread={conversation.unread}
                    archived={showArchived}
                    busy={busyConversationIds.has(conversation.id)}
                    onChange={change => void changeConversation(conversation.id, change)}
                  />
                </li>;
              }
              if (!app) return null;
              const thread = threadsByApp.get(app.id) ?? null;
              const selected = app.id === currentApp?.id;
              const preview = conversationPreview(app, thread, conversationPreviews.get(conversationId ?? ""));
              return (
                <li className="a3-conversation-item" data-unread={(conversation?.unread ?? thread?.unread) || undefined}
                  key={app.id}>
                  <Button
                    type="button"
                    variant="ghost"
                    className="a3-conversation"
                    data-kind={app.identityStatus === "pending" ? "chat" : "app"}
                    aria-current={selected ? "page" : undefined}
                    title={conversation?.title ?? app.name ?? thread?.title ?? "New chat"}
                    aria-label={`${conversation?.title ?? app.name ?? thread?.title ?? "New chat"}${app.ownerUserId !== session.userId ? " — Shared app" : ""}${(conversation?.unread ?? thread?.unread) ? " — Unread" : ""}`}
                    onClick={() => onSelectApp(app, conversationId ? { threadId: conversationId } : undefined)}
                  >
                    <span className="a3-sidebar-app-icon">
                      <AppIcon app={app} />
                      {working ? (
                        <span className="a3-sidebar-app-progress" title="Working">
                          <Spinner aria-label={`${app.name ?? thread?.title ?? "Chat"} is working`} />
                        </span>
                      ) : attention ? (
                        <span className="a3-sidebar-app-progress" data-status="attention" role="img" aria-label="Needs attention" title="Needs attention"><CircleAlert aria-hidden="true" /></span>
                      ) : null}
                    </span>
                    <strong className="a3-sidebar-app-name">
                      {conversation?.title ?? app.name ?? thread?.title ?? "New chat"}
                    </strong>
                    <span className="a3-sidebar-app-preview" title={preview}>
                      {preview}
                    </span>
                    <small className="a3-sidebar-app-time">
                      {sidebarOrderTimestamp(order) ? relativeDate(sidebarOrderTimestamp(order)) : "New"}
                    </small>
                  </Button>
                  {conversationId ? <ConversationActions
                    bootstrap={bootstrap}
                    title={conversation?.title ?? app.name ?? thread?.title ?? "New chat"}
                    pinned={conversation?.pinned ?? false}
                    unread={conversation?.unread ?? thread?.unread ?? false}
                    archived={showArchived}
                    {...(app.identityStatus === "assigned" ? { app } : {})}
                    busy={busyConversationIds.has(conversationId)}
                    onChange={change => void changeConversation(conversationId, change)}
                  /> : null}
                </li>
              );
            })}
            {recentEntries.length === 0 ? (
              <li className="a3-conversations-empty">
                {showArchived
                  ? loadingArchived ? "Loading archived chats…" : "No archived chats."
                  : query ? `No chats match “${query}”.` : "No chats yet."}
              </li>
            ) : null}
          </ul>
        </ScrollArea>
        {!showArchived ? (
          <Button type="button" variant="ghost" className="a3-archived-chats"
            onClick={() => {
              setQuery("");
              setSearchOpen(false);
              setShowArchived(true);
            }}>
            <Archive aria-hidden="true" /> Archived
          </Button>
        ) : null}
        {actionError ? <p className="a3-sidebar-action-error" role="alert">{actionError}</p> : null}
      </nav>

      <ProfileMenu bootstrap={bootstrap} session={session} />
    </div>
  );
}

function ConversationActions({
  bootstrap,
  title,
  pinned,
  unread,
  archived,
  app,
  busy,
  onChange,
}: {
  bootstrap: AgentBootstrap;
  title: string;
  pinned: boolean;
  unread: boolean;
  archived: boolean;
  app?: AgentAppView;
  busy: boolean;
  onChange: (change: Parameters<typeof updateConversationSidebar>[1]) => void;
}) {
  return (
    <div className="a3-conversation-actions">
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={busy}
          render={<Button type="button" size="icon" variant="ghost"
            className="a3-icon-button a3-conversation-menu-trigger"
            aria-label={`Conversation options for ${title}`} />}
        >
          {busy ? <Spinner aria-label={`Changing ${title}`} /> : <MoreHorizontal aria-hidden="true" />}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="a3-conversation-menu a3-sidebar-conversation-menu">
          <DropdownMenuGroup>
            {archived ? (
              <DropdownMenuItem onClick={() => onChange({ archived: false })}>
                <ArchiveRestore aria-hidden="true" /> Restore
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem variant="destructive" onClick={() => onChange({ archived: true })}>
                <Archive aria-hidden="true" /> Archive
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => {
              const next = window.prompt("Conversation name", title)?.trim();
              if (next && next !== title) onChange({ title: next });
            }}>
              <Pencil aria-hidden="true" /> Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onChange({ pinned: !pinned })}>
              {pinned ? <PinOff aria-hidden="true" /> : <Pin aria-hidden="true" />}
              {pinned ? "Unpin from Top" : "Pin to Top"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onChange({ unread: !unread })}>
              {unread ? <MailOpen aria-hidden="true" /> : <Mail aria-hidden="true" />}
              {unread ? "Mark read" : "Mark unread"}
            </DropdownMenuItem>
          </DropdownMenuGroup>
          {app ? <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem disabled={!app.appUrl}
                render={app.appUrl ? <a href={app.appUrl} target="_blank" rel="noreferrer" /> : undefined}>
                <ExternalLink aria-hidden="true" /> Open App
              </DropdownMenuItem>
              <DropdownMenuItem render={<a href={`${bootstrap.appsUrl}/${encodeURIComponent(app.id)}`} />}>
                <LayoutDashboard aria-hidden="true" /> Open App Dashboard
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </> : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export type SidebarConversationOrder = {
  id: string;
  pinned: boolean;
  attention: boolean;
  working: boolean;
  lastUserMessageAt: string | null;
  lastAgentMessageAt: string | null;
  fallbackAt: string;
};

export function compareSidebarConversationOrder(
  left: SidebarConversationOrder,
  right: SidebarConversationOrder,
): number {
  const rank = (value: SidebarConversationOrder) => value.attention ? 0 : value.working ? 1 : 2;
  return Number(right.pinned) - Number(left.pinned) || rank(left) - rank(right) ||
    timestamp(sidebarOrderTimestamp(right)) - timestamp(sidebarOrderTimestamp(left)) ||
    left.id.localeCompare(right.id);
}

function sidebarOrderTimestamp(value: SidebarConversationOrder): string {
  if (value.attention) return mostRecent(value.lastUserMessageAt, value.lastAgentMessageAt, value.fallbackAt);
  if (value.working) return value.lastUserMessageAt ?? value.fallbackAt;
  return value.lastAgentMessageAt ?? value.fallbackAt;
}

function mostRecent(...values: Array<string | null>): string {
  return values.reduce<string>((latest, value) =>
    timestamp(value ?? "") > timestamp(latest) ? value! : latest, "");
}

function timestamp(value: string): number {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function MessageSearchResults({
  apps,
  results,
  searching,
  onOpen,
}: {
  apps: readonly AgentAppView[];
  results: readonly ConversationSearchResult[];
  searching: boolean;
  onOpen: (app: AgentAppView, result: ConversationSearchResult) => void;
}) {
  const appById = new Map(apps.map((app) => [app.id, app]));
  const visible = results
    .flatMap((result) => {
      const app = appById.get(result.appId);
      return app ? [{ app, result }] : [];
    })
    .slice(0, 8);
  if (searching) {
    return <p className="a3-search-status">Searching messages…</p>;
  }
  if (visible.length === 0) return null;
  return (
    <div className="a3-message-results" aria-label="Message results">
      <h3>Messages</h3>
      <ul>
        {visible.map(({ app, result }) => (
          <li key={result.messageId}>
            <Button type="button" variant="ghost" onClick={() => onOpen(app, result)}>
              <strong>{app.name}</strong>
              <span>{searchExcerpt(result.excerpt)}</span>
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function searchExcerpt(value: string): string {
  const compact = value.replace(/\s+/g, " ").trim();
  return compact.length > 120 ? `${compact.slice(0, 117)}…` : compact;
}

/** One chat-list line: the message without Markdown syntax, such as "## " headings. */
export function plainPreview(text: string | null | undefined): string {
  return (text ?? "")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*(?:[-*+]|\d+\.)\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    // Paired emphasis and code marks; single underscores stay, as in file_names.
    .replace(/(\*\*|__|~~|\*|`+)(?=\S)([^\n]*?\S)\1/g, "$2")
    .replace(/\s+/g, " ")
    .trim();
}

function conversationPreview(
  app: AgentAppView,
  thread: ConversationThread | null,
  livePreview?: string,
): string {
  const streamed = plainPreview(livePreview);
  if (streamed) return streamed;
  const latest = plainPreview(thread?.latestMessage);
  if (latest) return latest;
  if (app.state === "failed") return "Needs attention";
  if (thread) return thread.title;
  return app.identityStatus === "pending"
    ? "Tell Agent what you want to explore"
    : "Start the conversation";
}

export function filterSidebarApps(
  apps: readonly AgentAppView[],
  threadsByApp: ReadonlyMap<string, ConversationThread | null>,
  query: string,
): readonly AgentAppView[] {
  const term = query.trim().toLocaleLowerCase();
  if (!term) return apps;
  return apps.filter((app) => {
    const thread = threadsByApp.get(app.id);
    return (
      (app.name ?? "New chat").toLocaleLowerCase().includes(term) ||
      Boolean(thread?.title.toLocaleLowerCase().includes(term)) ||
      Boolean(thread?.latestMessage?.toLocaleLowerCase().includes(term))
    );
  });
}

export function Health({
  app,
  working,
}: {
  app: AgentAppView;
  working: boolean;
}) {
  const health = working
    ? "working"
    : app.state === "failed"
      ? "attention"
      : "healthy";
  const label = working
    ? "Working now"
    : health === "attention"
      ? "Needs attention"
      : app.activeDeploymentId
        ? "Up to date"
        : "Ready to build";
  return (
    <Badge
      className="a3-health"
      data-health={health}
      tone={health === "healthy" ? "neutral" : health === "working" ? "info" : "warning"}
    >
      {working ? <Spinner aria-label="App work in progress" /> : null}
      {label}
    </Badge>
  );
}

export function ProfileMenu({
  bootstrap,
  session,
}: {
  bootstrap: AgentBootstrap;
  session: SessionView;
}) {
  const { productName } = agentBrand();
  const displayName =
    session.profile.displayName ??
    session.profile.email ??
    `${productName} account`;
  const initial =
    displayName.trim().charAt(0).toUpperCase() ||
    productName.charAt(0).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button type="button" variant="ghost" className="a3-account" />}
      >
        <Avatar>
          {session.profile.avatarUrl ? (
            <AvatarImage src={session.profile.avatarUrl} alt="" />
          ) : null}
          <AvatarFallback>{initial}</AvatarFallback>
        </Avatar>
        <span className="a3-account-text">
          <strong>{session.profile.displayName ?? "Your account"}</strong>
          <small>{session.profile.email ?? "Signed in"}</small>
        </span>
        <ChevronDown aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="a3-profile-menu">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="a3-profile-summary">
            <strong>{session.profile.displayName ?? "Your account"}</strong>
            <span>{session.profile.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuItem render={<a href={bootstrap.preferencesUrl} />}>
            Preferences
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <form method="post" action={bootstrap.signOutUrl}>
          <DropdownMenuItem
            render={<Button type="submit" variant="ghost" className="a3-profile-sign-out" />}
          >
            Sign out
          </DropdownMenuItem>
        </form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function relativeDate(value: string): string {
  const difference = Date.now() - Date.parse(value);
  if (!Number.isFinite(difference) || difference < 0) return "Now";
  const minutes = Math.floor(difference / 60_000);
  if (minutes < 1) return "Now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}
